import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import {
  X,
  Trash2,
  Mail,
  Phone,
  MessageCircle,
  Building2,
  User,
  MapPin,
  Edit2,
  Save,
  XCircle,
  FileText,
} from 'lucide-react'
import { useClient, useUpdateClient, useDeleteClient, useClientActivities, useClientProjects } from '../../api/clients'
import { useDocuments } from '../../api/documents'
import { Badge } from '../ui/Badge'
import { ConfirmModal } from '../ui/ConfirmModal'
import { cn, formatDate } from '../../lib/utils'

interface ClientDetailModalProps {
  clientId: string
  isOpen: boolean
  onClose: () => void
}

export default function ClientDetailModal({ clientId, isOpen, onClose }: ClientDetailModalProps) {
  const { data: client, isLoading } = useClient(clientId)
  const updateClient = useUpdateClient()
  const deleteClient = useDeleteClient()
  const { data: activities, isLoading: activitiesLoading } = useClientActivities(clientId)
  const { data: projectsData } = useClientProjects(clientId)

  const { data: clientDocuments = [] } = useDocuments({ clientId })

  const [notes, setNotes] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [activeTab, setActiveTab] = useState<'info' | 'projects' | 'documents' | 'history'>('info')
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState({
    contactName: '',
    email: '',
    phone: '',
    telegram: '',
    whatsapp: '',
    companyName: '',
    inn: '',
    ogrn: '',
    legalAddress: '',
  })

  useEffect(() => {
    if (client) {
      setNotes(client.notes || '')
    }
  }, [client])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !showDeleteConfirm) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose, showDeleteConfirm])

  const handleNotesBlur = () => {
    if (client && notes !== (client.notes || '')) {
      updateClient.mutate({ id: clientId, data: { notes } })
    }
  }

  const handleDelete = () => {
    deleteClient.mutate(clientId, {
      onSuccess: () => {
        setShowDeleteConfirm(false)
        onClose()
      },
    })
  }

  const initEdit = () => {
    if (client) {
      setEditData({
        contactName: client.contactName || '',
        email: client.email || '',
        phone: client.phone || '',
        telegram: client.telegram || '',
        whatsapp: client.whatsapp || '',
        companyName: client.companyName || '',
        inn: client.inn || '',
        ogrn: client.ogrn || '',
        legalAddress: client.legalAddress || '',
      })
      setIsEditing(true)
    }
  }

  const handleSaveEdit = () => {
    updateClient.mutate(
      {
        id: clientId,
        data: {
          contactName: editData.contactName,
          email: editData.email || null,
          phone: editData.phone || null,
          telegram: editData.telegram || null,
          whatsapp: editData.whatsapp || null,
          companyName: editData.companyName || null,
          inn: editData.inn || null,
          ogrn: editData.ogrn || null,
          legalAddress: editData.legalAddress || null,
        },
      },
      { onSuccess: () => setIsEditing(false) }
    )
  }

  const displayName = client
    ? client.type === 'company' && client.companyName
      ? client.companyName
      : client.contactName
    : ''

  const inputClassName = 'w-full px-3 py-1.5 bg-bg-tertiary border border-border-medium rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-lime/50'

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative bg-bg-card border border-border-medium backdrop-blur-glass w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl flex flex-col"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin w-8 h-8 border-2 border-accent-lime border-t-transparent rounded-full" />
              </div>
            ) : !client ? (
              <div className="text-center py-20 text-text-tertiary">
                Клиент не найден
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-6 pb-3 border-b border-white/10">
                  <h2 className="text-xl font-bold text-white truncate pr-4">{displayName}</h2>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={isEditing ? handleSaveEdit : initEdit}
                      disabled={updateClient.isPending}
                      className="w-8 h-8 rounded-lg bg-white/5 hover:bg-accent-lime/20 flex items-center justify-center text-text-secondary hover:text-accent-lime transition-colors"
                      title={isEditing ? 'Сохранить' : 'Редактировать'}
                    >
                      {isEditing ? <Save className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                    </button>
                    {isEditing && (
                      <button
                        onClick={() => setIsEditing(false)}
                        className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-text-secondary hover:text-white transition-colors"
                        title="Отмена"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/20 flex items-center justify-center text-text-secondary hover:text-red-400 transition-colors"
                      title="Удалить"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={onClose}
                      className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-text-secondary hover:text-white transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 px-6 py-3">
                  {(['info', 'projects', 'documents', 'history'] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={cn(
                        'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                        activeTab === tab
                          ? 'bg-accent-lime/15 text-accent-lime'
                          : 'text-text-secondary hover:text-white hover:bg-white/5'
                      )}
                    >
                      {tab === 'info' ? 'Информация' : tab === 'projects' ? `Проекты (${projectsData?.items?.length || 0})` : tab === 'documents' ? `Документы (${clientDocuments.length})` : 'История'}
                    </button>
                  ))}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Info Tab */}
                  {activeTab === 'info' && (
                    <>
                      {/* Type Badge */}
                      <div>
                        {client.type === 'company' ? (
                          <Badge variant="info">
                            <Building2 className="w-3 h-3 mr-1" />
                            Компания
                          </Badge>
                        ) : (
                          <Badge variant="violet">
                            <User className="w-3 h-3 mr-1" />
                            Физлицо
                          </Badge>
                        )}
                      </div>

                      {/* Company Info */}
                      {client.type === 'company' && (
                        <>
                          <div className="h-px bg-white/10" />
                          <div>
                            <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                              Информация о компании
                            </label>
                            <div className="space-y-2">
                              {isEditing ? (
                                <>
                                  <div>
                                    <span className="text-xs text-text-tertiary">Название</span>
                                    <input
                                      value={editData.companyName}
                                      onChange={(e) => setEditData(prev => ({ ...prev, companyName: e.target.value }))}
                                      className={inputClassName}
                                      placeholder="Название компании"
                                    />
                                  </div>
                                  <div>
                                    <span className="text-xs text-text-tertiary">ИНН</span>
                                    <input
                                      value={editData.inn}
                                      onChange={(e) => setEditData(prev => ({ ...prev, inn: e.target.value }))}
                                      className={inputClassName}
                                      placeholder="ИНН"
                                    />
                                  </div>
                                  <div>
                                    <span className="text-xs text-text-tertiary">ОГРН</span>
                                    <input
                                      value={editData.ogrn}
                                      onChange={(e) => setEditData(prev => ({ ...prev, ogrn: e.target.value }))}
                                      className={inputClassName}
                                      placeholder="ОГРН"
                                    />
                                  </div>
                                </>
                              ) : (
                                <>
                                  {client.companyName && (
                                    <div className="text-sm text-text-primary">
                                      <span className="text-text-tertiary mr-2">Название:</span>
                                      {client.companyName}
                                    </div>
                                  )}
                                  {client.inn && (
                                    <div className="text-sm text-text-primary">
                                      <span className="text-text-tertiary mr-2">ИНН:</span>
                                      <span className="font-mono">{client.inn}</span>
                                    </div>
                                  )}
                                  {client.ogrn && (
                                    <div className="text-sm text-text-primary">
                                      <span className="text-text-tertiary mr-2">ОГРН:</span>
                                      <span className="font-mono">{client.ogrn}</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </>
                      )}

                      <div className="h-px bg-white/10" />

                      {/* Contact Info */}
                      <div>
                        <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                          Контакты
                        </label>
                        <div className="space-y-2">
                          {isEditing ? (
                            <>
                              <div>
                                <span className="text-xs text-text-tertiary">Контактное лицо</span>
                                <input
                                  value={editData.contactName}
                                  onChange={(e) => setEditData(prev => ({ ...prev, contactName: e.target.value }))}
                                  className={inputClassName}
                                  placeholder="Имя контактного лица"
                                />
                              </div>
                              <div>
                                <span className="text-xs text-text-tertiary">Email</span>
                                <input
                                  value={editData.email}
                                  onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                                  className={inputClassName}
                                  placeholder="email@example.com"
                                  type="email"
                                />
                              </div>
                              <div>
                                <span className="text-xs text-text-tertiary">Телефон</span>
                                <input
                                  value={editData.phone}
                                  onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                                  className={inputClassName}
                                  placeholder="+7 999 123-45-67"
                                />
                              </div>
                              <div>
                                <span className="text-xs text-text-tertiary">Telegram</span>
                                <input
                                  value={editData.telegram}
                                  onChange={(e) => setEditData(prev => ({ ...prev, telegram: e.target.value }))}
                                  className={inputClassName}
                                  placeholder="@username"
                                />
                              </div>
                              <div>
                                <span className="text-xs text-text-tertiary">WhatsApp</span>
                                <input
                                  value={editData.whatsapp}
                                  onChange={(e) => setEditData(prev => ({ ...prev, whatsapp: e.target.value }))}
                                  className={inputClassName}
                                  placeholder="+7 999 123-45-67"
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              {client.contactName && (
                                <div className="flex items-center gap-2 text-sm text-text-primary">
                                  <User className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                                  <span>{client.contactName}</span>
                                </div>
                              )}
                              {client.email && (
                                <div className="flex items-center gap-2 text-sm text-text-primary">
                                  <Mail className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                                  <a
                                    href={`mailto:${client.email}`}
                                    className="hover:text-accent-lime transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {client.email}
                                  </a>
                                </div>
                              )}
                              {client.phone && (
                                <div className="flex items-center gap-2 text-sm text-text-primary">
                                  <Phone className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                                  <a
                                    href={`tel:${client.phone}`}
                                    className="hover:text-accent-lime transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {client.phone}
                                  </a>
                                </div>
                              )}
                              {client.telegram && (
                                <div className="flex items-center gap-2 text-sm text-text-primary">
                                  <MessageCircle className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                                  <a
                                    href={`https://t.me/${client.telegram.replace('@', '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-accent-lime transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    @{client.telegram.replace('@', '')}
                                  </a>
                                </div>
                              )}
                              {client.whatsapp && (
                                <div className="flex items-center gap-2 text-sm text-text-primary">
                                  <Phone className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                                  <a
                                    href={`https://wa.me/${client.whatsapp.replace(/[^0-9]/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-accent-lime transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    WhatsApp: {client.whatsapp}
                                  </a>
                                </div>
                              )}
                              {!client.contactName && !client.email && !client.phone && !client.telegram && !client.whatsapp && (
                                <p className="text-sm text-text-tertiary">Контакты не указаны</p>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Legal Address */}
                      {(client.type === 'company') && (
                        <>
                          <div className="h-px bg-white/10" />
                          <div>
                            <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                              Юридический адрес
                            </label>
                            {isEditing ? (
                              <input
                                value={editData.legalAddress}
                                onChange={(e) => setEditData(prev => ({ ...prev, legalAddress: e.target.value }))}
                                className={inputClassName}
                                placeholder="Юридический адрес"
                              />
                            ) : client.legalAddress ? (
                              <div className="flex items-start gap-2 text-sm text-text-primary">
                                <MapPin className="w-4 h-4 text-text-tertiary flex-shrink-0 mt-0.5" />
                                <span>{client.legalAddress}</span>
                              </div>
                            ) : (
                              <p className="text-sm text-text-tertiary">Не указан</p>
                            )}
                          </div>
                        </>
                      )}

                      <div className="h-px bg-white/10" />

                      {/* Notes */}
                      <div>
                        <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                          Заметки
                        </label>
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          onBlur={handleNotesBlur}
                          className="w-full h-32 px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent-lime/50 focus:ring-1 focus:ring-accent-lime/20 resize-none"
                          placeholder="Добавьте заметки о клиенте..."
                        />
                      </div>

                      {/* Created date */}
                      <div className="h-px bg-white/10" />
                      <div className="text-xs text-text-tertiary">
                        Создан {formatDate(client.createdAt)}
                      </div>
                    </>
                  )}

                  {/* Projects Tab */}
                  {activeTab === 'projects' && (
                    <div className="space-y-3">
                      {projectsData?.items?.length ? (
                        projectsData.items.map((project: any) => {
                          const totalAmount = project.payments?.reduce((sum: number, p: any) => sum + parseFloat(p.amount), 0) || 0
                          const paidAmount = project.payments?.filter((p: any) => p.status === 'paid')
                            .reduce((sum: number, p: any) => sum + parseFloat(p.amount), 0) || 0
                          const paidPercent = totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0

                          return (
                            <a
                              key={project.id}
                              href={`/projects/${project.id}`}
                              className="block p-3 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-white">{project.name}</span>
                                {project.stage && (
                                  <span
                                    className="text-xs px-2 py-0.5 rounded-full border"
                                    style={{
                                      backgroundColor: `${project.stage.color}20`,
                                      borderColor: `${project.stage.color}40`,
                                      color: project.stage.color,
                                    }}
                                  >
                                    {project.stage.name}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center justify-between text-sm text-text-tertiary">
                                {project.budget ? (
                                  <span>{parseFloat(project.budget).toLocaleString('ru-RU')} ₽</span>
                                ) : <span />}
                                {totalAmount > 0 && (
                                  <span className={paidPercent === 100 ? 'text-green-400' : 'text-accent-orange'}>
                                    Оплата: {paidPercent}%
                                  </span>
                                )}
                              </div>
                            </a>
                          )
                        })
                      ) : (
                        <p className="text-center py-8 text-text-tertiary">Нет привязанных проектов</p>
                      )}
                    </div>
                  )}

                  {/* Documents Tab */}
                  {activeTab === 'documents' && (
                    <div className="space-y-3">
                      {clientDocuments.length > 0 ? (
                        clientDocuments.map((doc: any) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-lg"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <FileText className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-white truncate">{doc.name}</p>
                                <p className="text-xs text-text-tertiary">
                                  {doc.project?.name && <span>{doc.project.name} · </span>}
                                  {doc.type}
                                </p>
                              </div>
                            </div>
                            <span className={cn(
                              'text-xs px-2 py-0.5 rounded-full border flex-shrink-0',
                              doc.status === 'signed' && 'bg-green-500/10 text-green-400 border-green-500/20',
                              doc.status === 'draft' && 'bg-white/5 text-text-tertiary border-white/10',
                              doc.status === 'review' && 'bg-accent-orange/10 text-accent-orange border-accent-orange/20',
                              doc.status === 'sent' && 'bg-accent-blue/10 text-accent-blue border-accent-blue/20',
                              doc.status === 'archived' && 'bg-white/5 text-text-tertiary border-white/10',
                            )}>
                              {doc.status === 'draft' ? 'Черновик' :
                               doc.status === 'review' ? 'Проверка' :
                               doc.status === 'sent' ? 'Отправлен' :
                               doc.status === 'signed' ? 'Подписан' :
                               doc.status === 'archived' ? 'Архив' : doc.status}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-center py-8 text-text-tertiary">Нет документов</p>
                      )}
                    </div>
                  )}

                  {/* History Tab */}
                  {activeTab === 'history' && (
                    <div className="space-y-3">
                      {activitiesLoading ? (
                        <div className="text-center py-8 text-text-tertiary">Загрузка...</div>
                      ) : activities?.length ? (
                        activities.map((activity) => (
                          <div key={activity.id} className="flex gap-3 text-sm">
                            <div className="w-2 h-2 rounded-full bg-accent-lime mt-2 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-text-primary">{activity.content}</p>
                              <div className="flex items-center gap-2 mt-1 text-xs text-text-tertiary">
                                {activity.user && <span>{activity.user.username}</span>}
                                <span>{formatDate(activity.createdAt)}</span>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-center py-8 text-text-tertiary">Нет истории</p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </motion.div>

          {/* Delete Confirmation */}
          {client && (
            <ConfirmModal
              isOpen={showDeleteConfirm}
              title="Удалить клиента?"
              message={`Вы уверены, что хотите удалить клиента "${displayName}"? Это действие нельзя отменить.`}
              confirmText="Удалить"
              cancelText="Отмена"
              variant="danger"
              onConfirm={handleDelete}
              onCancel={() => setShowDeleteConfirm(false)}
              loading={deleteClient.isPending}
            />
          )}
        </div>
      )}
    </AnimatePresence>
  )
}
